import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EMPTY_SPLIT,
  ORDER_KIND_LABELS,
  PAYOUT_METHOD_LABELS,
  type OrderDTO,
  type OrderKind,
  type PayoutMethod,
  type SaleSplit,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Input,
  Kpi,
  PageHeader,
  Select,
  Skeleton,
  useToast,
} from '@bifeai/ui';
import { Bot, Building2, CircleDollarSign, KeyRound, Receipt, Repeat, Store, Trash2, Undo2, Users } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { useCan } from '../lib/session';
import { formatDateTime, formatUsd } from '../lib/format';
import { DateRangePicker, useDateRange } from '../components/DateRange';

const STATUS: Record<OrderDTO['status'], { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  PAID: { label: 'Cobrada', tone: 'success' },
  PENDING: { label: 'Sin pagar', tone: 'warning' },
  FAILED: { label: 'Rechazada', tone: 'danger' },
  EXPIRED: { label: 'Abandonada', tone: 'neutral' },
  REFUNDED: { label: 'Devuelta', tone: 'danger' },
};

/** Ventas de BifeAI: compras en la web, adicionales, sucursales y renovaciones; avisos y consultas. */
export function SalesPage() {
  const [kind, setKind] = useState<OrderKind | ''>('');
  const can = useCan();
  const qc = useQueryClient();
  const toast = useToast();
  const [deleting, setDeleting] = useState<OrderDTO | null>(null);
  const [refunding, setRefunding] = useState<OrderDTO | null>(null);
  const [reason, setReason] = useState('');
  const refund = useMutation({
    mutationFn: (id: string) => api.post(`/platform/billing/orders/${id}/refund`, { reason }),
    onSuccess: async () => {
      toast.success('Venta marcada como devuelta', 'Se anularon sus comisiones pendientes.');
      setRefunding(null);
      setReason('');
      await qc.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: (err) => toast.error('No se pudo marcar', errorMessage(err)),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/platform/billing/orders/${id}`),
    onSuccess: async () => {
      toast.success('Venta borrada');
      setDeleting(null);
      await qc.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: (err) => toast.error('No se pudo borrar', errorMessage(err)),
  });
  const range = useDateRange('month');
  const qs = range.qs({ kind: kind || undefined });
  const orders = useQuery({
    queryKey: ['billing', 'orders', qs],
    queryFn: () => api.get<OrderDTO[]>(`/platform/billing/orders${qs ? `?${qs}` : ''}`),
  });

  // Lo cobrado en el período (las devueltas no cuentan), repartido entre licencia, abonos por plan y bifi.
  const paid = (orders.data ?? []).filter((o) => o.status === 'PAID');
  const t: SaleSplit = paid.reduce(
    (acc, o) => {
      for (const k of Object.keys(acc) as (keyof SaleSplit)[]) acc[k] += o.split[k];
      return acc;
    },
    { ...EMPTY_SPLIT },
  );
  const total = paid.reduce((a, o) => a + o.amountCents, 0);
  const subs = t.subCarniceriaCents + t.subMultiCents + t.subFranchisesCents;
  const bifi = t.bifiSetupCents + t.bifiMonthlyCents;
  const newCustomers = paid.filter((o) => o.kind === 'PURCHASE').length;

  return (
    <>
      <PageHeader title="Ventas" subtitle="Todo lo que se cobra: compras en la web, módulos, sucursales, franquicias y renovaciones." />
      <Card>
        <CardHeader title={`Cobrado · ${range.label}`} actions={<DateRangePicker r={range} />} />
      </Card>
      <div style={{ height: 14 }} />
      <div className="bf-kpi-grid">
        <Kpi
          label="Cobrado en el período"
          tone="green"
          icon={<CircleDollarSign />}
          value={formatUsd(total)}
          delta={`${paid.length} cobros · ${newCustomers} ${newCustomers === 1 ? 'cliente nuevo' : 'clientes nuevos'}`}
          deltaMuted
        />
        <Kpi
          label="Ventas de licencia"
          icon={<KeyRound />}
          value={formatUsd(t.licenseCents)}
          delta="Licencias, adicionales y mantenimientos"
          deltaMuted
        />
        <Kpi label="Ventas de abonos" icon={<Repeat />} value={formatUsd(subs)} delta="Abono mensual de los tres planes" deltaMuted />
        <Kpi label="Ventas de bifi" icon={<Bot />} value={formatUsd(bifi)} delta="Activación y abono mensual" deltaMuted />
      </div>
      <div style={{ height: 14 }} />
      <div className="bf-kpi-grid">
        <Kpi label="Abono Carnicería" icon={<Store />} value={formatUsd(t.subCarniceriaCents)} />
        <Kpi label="Abono Multisucursal" icon={<Building2 />} value={formatUsd(t.subMultiCents)} />
        <Kpi label="Abono Franquicias" icon={<Users />} value={formatUsd(t.subFranchisesCents)} delta="Marca y franquiciados" deltaMuted />
        <Kpi label="bifi · activación" icon={<Bot />} value={formatUsd(t.bifiSetupCents)} delta="Pago de entrada" deltaMuted />
        <Kpi label="bifi · abono mensual" icon={<Bot />} value={formatUsd(t.bifiMonthlyCents)} delta="Por número de WhatsApp" deltaMuted />
      </div>
      <div style={{ height: 18 }} />
      <Card>
        <CardHeader
          title="Ventas"
          actions={
            <Select
              value={kind}
              onChange={(e) => setKind(e.target.value as OrderKind | '')}
              aria-label="Tipo de venta"
              style={{ minWidth: 180 }}
            >
              <option value="">Todas</option>
              {(Object.keys(ORDER_KIND_LABELS) as OrderKind[]).map((k) => (
                <option key={k} value={k}>
                  {ORDER_KIND_LABELS[k]}
                </option>
              ))}
            </Select>
          }
        />
        <CardBody flush>
          {orders.isPending ? (
            <Skeleton style={{ height: 160 }} />
          ) : !orders.data?.length ? (
            <EmptyState
              icon={<Receipt />}
              title="No hay ventas en este período"
              text="Probá con otras fechas. Cuando alguien compre en bifeai.com o sume algo desde el sistema, aparece acá."
            />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Cliente</th>
                    <th>Qué</th>
                    <th>Vendedor</th>
                    <th>Estado</th>
                    <th style={{ textAlign: 'right' }}>Importe</th>
                    {can('sales.delete') && <th aria-label="Acciones" />}
                  </tr>
                </thead>
                <tbody>
                  {orders.data.map((o) => (
                    <tr key={o.id}>
                      <td className="bf-cell-sub">{formatDateTime(o.paidAt ?? o.createdAt)}</td>
                      <td>
                        <span className="bf-cell-main">{o.tenantName ?? o.buyerName ?? '—'}</span>
                        {o.buyerEmail && <div className="bf-cell-sub">{o.buyerEmail}</div>}
                      </td>
                      <td>
                        <Badge>{ORDER_KIND_LABELS[o.kind]}</Badge> <span className="bf-cell-sub">{o.description}</span>
                      </td>
                      <td className="bf-cell-sub">{o.sellerName ?? '—'}</td>
                      <td>
                        <Badge tone={STATUS[o.status].tone}>{STATUS[o.status].label}</Badge>
                        {o.manualMethod && (
                          <div className="bf-cell-sub">A mano · {PAYOUT_METHOD_LABELS[o.manualMethod as PayoutMethod] ?? o.manualMethod}</div>
                        )}
                      </td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {formatUsd(o.amountCents)}
                      </td>
                      {can('sales.delete') && (
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {o.status === 'PAID' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label="Marcar como devuelta"
                              title="Marcar como devuelta"
                              icon={<Undo2 />}
                              onClick={() => setRefunding(o)}
                            />
                          )}
                          <Button size="sm" variant="ghost" aria-label="Borrar venta" icon={<Trash2 />} onClick={() => setDeleting(o)} />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      {refunding && (
        <Dialog open onOpenChange={(o) => !o && setRefunding(null)} title="Marcar la venta como devuelta">
          <div className="bf-form">
            <p style={{ margin: 0 }}>
              <strong>{refunding.tenantName ?? refunding.buyerName ?? 'Venta'}</strong> · {refunding.description} · {formatUsd(refunding.amountCents)}
            </p>
            <Callout tone="warning">
              Deja de contar como venta y se anulan sus comisiones pendientes. Si alguna comisión ya se pagó, te queda un aviso para descontarla. La
              devolución de la plata se hace en Stripe (o a mano): esto no la hace.
            </Callout>
            <Field label="Motivo">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} autoFocus />
            </Field>
            <div className="bf-form-actions">
              <Button onClick={() => setRefunding(null)}>Cancelar</Button>
              <Button variant="danger" loading={refund.isPending} disabled={reason.trim().length < 3} onClick={() => refund.mutate(refunding.id)}>
                Marcar devuelta
              </Button>
            </div>
          </div>
        </Dialog>
      )}
      {deleting && (
        <Dialog open onOpenChange={(o) => !o && setDeleting(null)} title="¿Borrar esta venta del panel?">
          <div className="bf-form">
            <p style={{ margin: 0 }}>
              <strong>{deleting.tenantName ?? deleting.buyerName ?? 'Venta'}</strong> · {deleting.description} · {formatUsd(deleting.amountCents)}
            </p>
            <Callout tone="warning">
              Solo se borra del panel (sirve para pruebas o errores). No devuelve plata ni toca nada en Stripe. Si querés dar de baja al cliente,
              archivalo desde Clientes.
            </Callout>
            <div className="bf-form-actions">
              <Button onClick={() => setDeleting(null)}>Cancelar</Button>
              <Button variant="danger" loading={del.isPending} onClick={() => del.mutate(deleting.id)}>
                Borrar venta
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}
